import { ArrowRight, DollarSign, Home, TrendingUp } from 'lucide-react';
import { FinancialInputs } from '../types';
import InputGroup from './InputGroup';
import { formatCurrency0 } from '../utils/format';

interface BasePropertyFormProps {
  propertyName: string;
  address: string;
  ownershipEntity: string;
  ownershipEntityOptions: string[];
  baseYear: number;
  inputs: FinancialInputs;
  isSaved: boolean;
  step: 'core' | 'operations' | 'forecast';
  mode: 'create' | 'edit';
  onNameChange: (value: string) => void;
  onAddressChange: (value: string) => void;
  onOwnershipEntityChange: (value: string) => void;
  onBaseYearChange: (value: number) => void;
  onInputChange: <K extends keyof FinancialInputs>(key: K, value: FinancialInputs[K]) => void;
  onContinue: () => void;
  onBack: () => void;
  onSave: () => void;
}

const BasePropertyForm = (props: BasePropertyFormProps) => {
  const { propertyName, address, ownershipEntity, ownershipEntityOptions, baseYear, inputs, isSaved, step, mode, onNameChange, onAddressChange, onOwnershipEntityChange, onBaseYearChange, onInputChange, onContinue, onBack, onSave } = props;
  const monthlyRent = inputs.annualRent / 12;
  const upfrontCashInvestment = inputs.downPayment + inputs.acquisitionCosts + inputs.lenderFees + inputs.initialCapex + inputs.propertyReserves + inputs.portfolioReservesAllocated;
  const isCoreStep = step === 'core';
  const isOperationsStep = step === 'operations';
  const isForecastStep = step === 'forecast';
  const title = isCoreStep
    ? mode === 'edit' ? 'Edit asset basics.' : 'Add a property first.'
    : isOperationsStep
      ? mode === 'edit' ? 'Update operating inputs.' : 'Add operating inputs.'
      : 'Set forecast assumptions.';
  const subtitle = isCoreStep
    ? 'Capture the core underwriting inputs first, then continue to operating detail before opening the dashboard.'
    : isOperationsStep
      ? 'These inputs represent the current operating year snapshot and become the base for future forecast years.'
      : 'These assumptions drive how rent, expenses, and value evolve over time.';
  const stepLabel = isCoreStep ? 'Step 1 of 3' : isOperationsStep ? 'Step 2 of 3' : 'Step 3 of 3';
  const nextButtonLabel = isCoreStep ? 'Next: Operating Inputs' : isOperationsStep ? 'Next: Forecast Assumptions' : (isSaved ? 'Save Property' : 'Create Asset');
  const helperTitle = isCoreStep ? 'Create asset' : isOperationsStep ? 'Complete operating model' : 'Set forecast path';
  const helperCopy = isCoreStep
    ? 'Set the explicit upfront cash you plan to invest. Nothing in this step is applied behind the scenes anymore.'
    : isOperationsStep
      ? 'Capture the current operating assumptions that should flow into NOI, cash flow, and reserves.'
      : 'Set the assumptions that drive rent growth, expense growth, and value growth. Hold length and implied exit cap are derived automatically.';
  const entityOptions = ownershipEntityOptions.includes(ownershipEntity) || !ownershipEntity
    ? ownershipEntityOptions
    : [...ownershipEntityOptions, ownershipEntity];
  const isCustomOwnershipEntity = Boolean(ownershipEntity && !ownershipEntityOptions.includes(ownershipEntity));
  const handleLandAllocationChange = (value: number) => {
    const nextLandPercent = Math.max(0, Math.min(100, Math.round(value * 100) / 100));
    onInputChange('landAllocationPercent', nextLandPercent);
    onInputChange('buildingAllocationPercent', Math.max(0, Math.min(100, 100 - nextLandPercent)));
  };
  const handleBuildingAllocationChange = (value: number) => {
    const nextBuildingPercent = Math.max(0, Math.min(100, Math.round(value * 100) / 100));
    onInputChange('buildingAllocationPercent', nextBuildingPercent);
    onInputChange('landAllocationPercent', Math.max(0, Math.min(100, 100 - nextBuildingPercent)));
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-[2.5rem] border border-slate-800/70 bg-slate-900/80 shadow-2xl shadow-black/30 overflow-hidden">
          <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_0.75fr]">
            <div className="p-8 lg:p-12 border-b xl:border-b-0 xl:border-r border-slate-800/70">
              <div className="flex items-center justify-between gap-4 mb-8">
                <div>
                  <p className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.24em]">{mode === 'edit' ? 'Edit Asset' : 'Create Asset'}</p>
                  <h1 className="mt-3 text-4xl font-black tracking-tight text-white">{title}</h1>
                  <p className="mt-3 text-sm text-slate-400 max-w-2xl">{subtitle}</p>
                </div>
                <span className={`px-3 py-1.5 rounded-full border text-[9px] font-black uppercase tracking-widest ${isSaved ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}`}>
                  {stepLabel}
                </span>
              </div>

              {isCoreStep ? (
                <>
                  <section className="mb-10">
                    <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.22em] mb-5">Property Identity</h2>
                    <div className="grid grid-cols-1 gap-4">
                      <input
                        type="text"
                        value={propertyName}
                        onChange={(e: any) => onNameChange((e.target as HTMLInputElement).value)}
                        className="w-full rounded-[1.75rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-2xl font-black text-white outline-none transition-colors focus:border-emerald-500 placeholder-slate-600"
                        placeholder="Property name"
                      />
                      <input
                        type="text"
                        value={address}
                        onChange={(e: any) => onAddressChange((e.target as HTMLInputElement).value)}
                        className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-medium text-slate-200 outline-none transition-colors focus:border-emerald-500 placeholder-slate-500"
                        placeholder="Address or market"
                      />
                      <div className="grid grid-cols-1 gap-3">
                        <select
                          value={isCustomOwnershipEntity ? '__custom__' : ownershipEntity}
                          onChange={(e: any) => {
                            const nextValue = (e.target as HTMLSelectElement).value;
                            onOwnershipEntityChange(nextValue === '__custom__' ? '' : nextValue);
                          }}
                          className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-medium text-slate-200 outline-none transition-colors focus:border-emerald-500"
                        >
                          <option value="">No ownership entity selected</option>
                          {entityOptions.map((entity) => (
                            <option key={entity} value={entity}>{entity}</option>
                          ))}
                          <option value="__custom__">Add new entity...</option>
                        </select>
                        {(isCustomOwnershipEntity || ownershipEntity === '') && (
                          <input
                            type="text"
                            value={ownershipEntity}
                            onChange={(e: any) => onOwnershipEntityChange((e.target as HTMLInputElement).value)}
                            className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-medium text-slate-200 outline-none transition-colors focus:border-emerald-500 placeholder-slate-500"
                            placeholder="Ownership entity / LLC"
                          />
                        )}
                      </div>
                      <div className="w-full md:w-60">
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Current Operating Year</label>
                        <input
                          type="number"
                          value={baseYear}
                          onChange={(e: any) => onBaseYearChange(Number((e.target as HTMLInputElement).value) || new Date().getFullYear())}
                          className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-black text-slate-200 outline-none transition-colors focus:border-emerald-500"
                        />
                      </div>
                    </div>
                    <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Current Snapshot</p>
                      <p className="mt-2 text-sm text-slate-400">These inputs represent the current operating year and are used as the base for future forecasts.</p>
                    </div>
                  </section>

                  <section className="mb-10">
                    <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.22em] mb-5 flex items-center gap-2">
                      <DollarSign size={12} className="text-emerald-500" /> Acquisition
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <InputGroup label="Purchase Price" value={inputs.purchasePrice} min={50000} max={5000000} step={10000} type="currency" onChange={(v) => onInputChange('purchasePrice', v)} />
                      <InputGroup label="Equity Contribution" value={inputs.downPayment} min={0} max={inputs.purchasePrice} step={5000} type="currency" onChange={(v) => onInputChange('downPayment', v)} />
                      <InputGroup label="Acquisition / Closing Costs" value={inputs.acquisitionCosts} min={0} max={250000} step={500} type="currency" onChange={(v) => onInputChange('acquisitionCosts', v)} />
                      <InputGroup label="Lender Fees / Points" value={inputs.lenderFees} min={0} max={100000} step={250} type="currency" onChange={(v) => onInputChange('lenderFees', v)} />
                      <InputGroup label="Initial Renovation / Initial CapEx" value={inputs.initialCapex} min={0} max={500000} step={500} type="currency" onChange={(v) => onInputChange('initialCapex', v)} />
                      <InputGroup label="Property Reserve Funding" value={inputs.propertyReserves} min={0} max={250000} step={500} type="currency" onChange={(v) => onInputChange('propertyReserves', v)} />
                      <InputGroup label="Portfolio Reserve Allocation" value={inputs.portfolioReservesAllocated} min={0} max={250000} step={500} type="currency" onChange={(v) => onInputChange('portfolioReservesAllocated', v)} />
                    </div>
                    <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Upfront Cash Investment</p>
                      <p className="mt-2 text-sm text-slate-400">Every upfront cost in the model is now explicit here. If you leave a line item at zero, it stays zero in total initial investment.</p>
                    </div>
                  </section>

                  <section className="mb-10">
                    <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.22em] mb-5">Tax Basis</h2>
                    <p className="mb-5 max-w-3xl text-sm text-slate-400">
                      Keep depreciation assumptions on the property record so tax reporting can reuse them automatically. These settings are informational and should be confirmed with your CPA.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <InputGroup label="Capital Improvements" value={inputs.capitalImprovements} min={0} max={5000000} step={1000} type="currency" onChange={(v) => onInputChange('capitalImprovements', v)} />
                      <InputGroup label="Land Allocation %" value={inputs.landAllocationPercent} min={0} max={100} step={1} type="percent" onChange={handleLandAllocationChange} />
                      <InputGroup label="Building Allocation %" value={inputs.buildingAllocationPercent} min={0} max={100} step={1} type="percent" onChange={handleBuildingAllocationChange} />
                      <div>
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Placed-In-Service Date</label>
                        <input
                          type="date"
                          value={inputs.placedInServiceDate || ''}
                          onChange={(e: any) => onInputChange('placedInServiceDate', (e.target as HTMLInputElement).value)}
                          className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-black text-slate-200 outline-none transition-colors focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Depreciation Method</label>
                        <select
                          value={inputs.depreciationMethod}
                          onChange={(e: any) => onInputChange('depreciationMethod', (e.target as HTMLSelectElement).value as FinancialInputs['depreciationMethod'])}
                          className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-black text-slate-200 outline-none transition-colors focus:border-emerald-500"
                        >
                          <option value="residential_rental_sl_27_5">Residential Rental 27.5 Year SL</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Default Assumption</p>
                      <p className="mt-2 text-sm text-slate-400">
                        If you do not have a separate land valuation yet, Wrennen will estimate depreciation using the purchase price with a default 20% land and 80% building split.
                      </p>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.22em] mb-5 flex items-center gap-2">
                      <TrendingUp size={12} className="text-emerald-500" /> Setup Preview
                    </h2>
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Acquisition Snapshot</p>
                      <p className="mt-2 text-2xl font-black text-white">{formatCurrency0(upfrontCashInvestment)}</p>
                      <p className="mt-2 text-sm text-slate-400">This is the explicit upfront cash currently modeled: equity, acquisition costs, lender fees, initial CapEx, and any reserve funding you choose to add.</p>
                    </div>
                  </section>
                </>
              ) : isOperationsStep ? (
                <section>
                  <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.22em] mb-5 flex items-center gap-2">
                    <Home size={12} className="text-amber-400" /> Current Year Operating Inputs
                  </h2>
                  <p className="mb-5 max-w-3xl text-sm text-slate-400">These inputs represent the current operating year and are used as the base for future forecasts.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <InputGroup label={`Current Annual Rent (${baseYear})`} value={inputs.annualRent} min={0} max={1000000} step={1000} type="currency" onChange={(v) => onInputChange('annualRent', v)} />
                    <InputGroup label={`Current Annual Taxes (${baseYear})`} value={inputs.annualTaxes} min={0} max={100000} step={250} type="currency" onChange={(v) => onInputChange('annualTaxes', v)} />
                    <InputGroup label={`Current Annual Insurance (${baseYear})`} value={inputs.annualInsurance} min={0} max={50000} step={100} type="currency" onChange={(v) => onInputChange('annualInsurance', v)} />
                    <InputGroup label={`Current Annual Maintenance (${baseYear})`} value={inputs.annualMaintenance} min={0} max={100000} step={250} type="currency" onChange={(v) => onInputChange('annualMaintenance', v)} />
                    <InputGroup label={`Current Annual HOA (${baseYear})`} value={inputs.annualHoa} min={0} max={100000} step={250} type="currency" onChange={(v) => onInputChange('annualHoa', v)} />
                    <InputGroup label="Property Management Rate" value={inputs.propertyManagementRate} min={0} max={25} step={0.25} type="percent" onChange={(v) => onInputChange('propertyManagementRate', v)} />
                    <InputGroup label="Utilities Monthly" value={inputs.utilitiesMonthly} min={0} max={25000} step={50} type="currency" onChange={(v) => onInputChange('utilitiesMonthly', v)} />
                    <InputGroup label="Other Income Monthly" value={inputs.otherIncomeMonthly} min={0} max={25000} step={50} type="currency" onChange={(v) => onInputChange('otherIncomeMonthly', v)} />
                  </div>
                  <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Why These Matter</p>
                    <p className="mt-2 text-sm text-slate-400">These current-year inputs now directly control NOI and cash flow. HOA, utilities, management, and other income are editable here instead of being left to hidden saved defaults.</p>
                  </div>

                  <div className="mt-6">
                    <p className="mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Annual Reserve Funding</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Reserve Funding Mode</label>
                        <select
                          value={inputs.reserveFundingMode}
                          onChange={(e: any) => onInputChange('reserveFundingMode', (e.target as HTMLSelectElement).value as FinancialInputs['reserveFundingMode'])}
                          className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-black text-slate-200 outline-none transition-colors focus:border-emerald-500"
                        >
                          <option value="fixed">Fixed Annual Reserve</option>
                          <option value="percent">Percent Of Rent</option>
                          <option value="off">Reserve Funding Off</option>
                        </select>
                      </div>
                      {inputs.reserveFundingMode === 'percent' ? (
                        <InputGroup label="Reserve Funding % Of Rent" value={inputs.reservePercentOfRent} min={0} max={25} step={0.25} type="percent" onChange={(v) => onInputChange('reservePercentOfRent', v)} />
                      ) : (
                        <InputGroup label="Annual CapEx Reserve" value={inputs.annualCapexReserve} min={0} max={100000} step={250} type="currency" onChange={(v) => onInputChange('annualCapexReserve', v)} />
                      )}
                    </div>
                    <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Reserve Logic</p>
                      <p className="mt-2 text-sm text-slate-400">
                        {inputs.reserveFundingMode === 'percent'
                          ? 'Reserve funding is modeled as a percent of scheduled rent and transferred into reserves monthly.'
                          : inputs.reserveFundingMode === 'off'
                            ? 'Recurring reserve funding is currently turned off.'
                            : 'Reserve funding is modeled as a fixed annual transfer into reserves.'}
                      </p>
                      <p className="mt-2 text-sm text-slate-500">
                        Starting reserves and sale-time reserve release are now explicit. If you leave them at zero or keep release off, the model will not silently add reserve capital.
                      </p>
                    </div>
                  </div>
                </section>
              ) : (
                <section>
                  <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.22em] mb-5 flex items-center gap-2">
                    <TrendingUp size={12} className="text-emerald-500" /> Forecast Assumptions
                  </h2>
                  <p className="mb-5 max-w-3xl text-sm text-slate-400">These assumptions drive how rent, expenses, value, and returns evolve over time.</p>

                  <div className="mb-8">
                    <p className="mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Growth</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <InputGroup label="Rent Growth Rate" value={inputs.rentGrowth} min={0} max={15} step={0.25} type="percent" onChange={(v) => onInputChange('rentGrowth', v)} />
                      <InputGroup label="Expense Growth Rate" value={inputs.expenseGrowth} min={0} max={15} step={0.25} type="percent" onChange={(v) => onInputChange('expenseGrowth', v)} />
                      <InputGroup label="Property Value Growth" value={inputs.appreciationRate} min={0} max={15} step={0.25} type="percent" onChange={(v) => onInputChange('appreciationRate', v)} />
                    </div>
                  </div>

                  <div className="mb-8">
                    <p className="mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Operations</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <InputGroup label="Vacancy Rate" value={inputs.vacancyRate} min={0} max={25} step={0.25} type="percent" onChange={(v) => onInputChange('vacancyRate', v)} />
                    </div>
                  </div>

                  <div>
                    <p className="mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Exit</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <InputGroup label="Exit Costs" value={inputs.saleCosts} min={0} max={12} step={0.25} type="percent" onChange={(v) => onInputChange('saleCosts', v)} />
                      <div>
                        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Reserve Release At Sale</label>
                        <select
                          value={inputs.includeReserveReleaseOnSale ? 'on' : 'off'}
                          onChange={(e: any) => onInputChange('includeReserveReleaseOnSale', (e.target as HTMLSelectElement).value === 'on')}
                          className="w-full rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-5 py-4 text-sm font-black text-slate-200 outline-none transition-colors focus:border-emerald-500"
                        >
                          <option value="off">Off</option>
                          <option value="on">Release Ending Reserve Balance</option>
                        </select>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Hold Period</p>
                        <p className="mt-2 text-sm font-black text-white">Auto</p>
                        <p className="mt-2 text-sm text-slate-400">Uses the linked debt term remaining. If no debt is linked, Wrennen uses a 30-year internal hold.</p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4 md:col-span-2">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Implied Exit Cap</p>
                        <p className="mt-2 text-sm font-black text-white">Calculated</p>
                        <p className="mt-2 text-sm text-slate-400">Calculated as that year&apos;s NOI divided by modeled current value. Modeled current value is purchase price grown by your property value growth rate.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Forecast Engine</p>
                    <p className="mt-2 text-sm text-slate-400">These assumptions feed rent and expense growth, vacancy drag, property value growth, terminal liquidity, and debt-length-based hold-period returns in the model.</p>
                  </div>
                </section>
              )}
            </div>

            <div className="p-8 lg:p-10 bg-slate-950/40">
              <div className="rounded-[2rem] border border-slate-800 bg-slate-950/80 p-6">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{stepLabel}</p>
                <h2 className="mt-3 text-2xl font-black tracking-tight text-white">{helperTitle}</h2>
                <p className="mt-3 text-sm text-slate-400">{helperCopy}</p>

                <div className="mt-6 space-y-4">
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                      {isCoreStep ? 'Upfront Cash Invested' : isOperationsStep ? `Current Annual Rent (${baseYear})` : 'Hold Period'}
                    </p>
                    <p className="text-2xl font-black text-white mt-2">
                      {isCoreStep ? formatCurrency0(upfrontCashInvestment) : isOperationsStep ? formatCurrency0(inputs.annualRent) : 'Auto'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">What Happens Next</p>
                    <div className="mt-3 space-y-3 text-sm text-slate-300">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 h-6 w-6 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-[10px] font-black text-emerald-300 flex items-center justify-center">1</div>
                        <p>{isCoreStep ? 'Continue into current-year operating inputs without losing what you entered here.' : isOperationsStep ? 'Set the forward-looking forecast assumptions that drive the model beyond the base year.' : 'Save the asset and lock in the current snapshot plus the forward forecast assumptions.'}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 h-6 w-6 rounded-full border border-slate-700 bg-slate-800 text-[10px] font-black text-slate-300 flex items-center justify-center">2</div>
                        <p>{isCoreStep ? 'Finish the current-year operating picture before setting forecast assumptions.' : isOperationsStep ? 'After that, save and move into the asset dashboard for current-year returns, future forecasts, and historical actuals.' : 'Move into the asset dashboard for current-year returns, future forecasts, and historical actuals.'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><Home size={12} className="text-amber-400" /> Later In The Dashboard</p>
                    <p className="mt-2 text-[12px] text-slate-400">Debt Desk linking, annual return tables, risk dashboards, reserves, and stress testing stay out of intake so the flow stays clear.</p>
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  {!isCoreStep && (
                    <button
                      onClick={onBack}
                      className="flex-1 rounded-2xl border border-slate-700 bg-slate-900 hover:bg-slate-800 text-white py-4 text-[11px] font-black uppercase tracking-[0.2em] transition-all"
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={isForecastStep ? onSave : onContinue}
                    className="flex-1 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white py-4 text-[11px] font-black uppercase tracking-[0.25em] shadow-lg shadow-emerald-950 transition-all flex items-center justify-center gap-2"
                  >
                    {nextButtonLabel} <ArrowRight size={14} />
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                  {isCoreStep ? 'This page now defines the full upfront cash investment with no hidden add-ons.' : isOperationsStep ? 'This page captures present-day operating reality and reserve funding only.' : 'Saving from this step sends you to the analytics dashboard.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasePropertyForm;
